import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";
import Flag from "../../assets/Flag";
import { Input } from "./Input";

type PhoneNoInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  inputClassName?: string;
  /** Overrides the default "Phone Number" placeholder (CMS-configurable forms). */
  placeholder?: string;
  /** Extra classes for the "+91" prefix label (e.g. color overrides) */
  prefixClassName?: string;
  /** Extra classes for the prefix container (e.g. a vertical divider after "+91") */
  prefixWrapperClassName?: string;
  label?: string;
  /** Flat white row + flag / +91 — matches Program Enrollments / embedded Lead Popup 7 card */
  embedCard?: boolean;
  /** Flat row for Lead Popup 7 desktop modal (IIT): #FFFFFF fill */
  desktopFigmaVariant?: boolean;
  /**
   * Drop the flag and "+91" and render the number field alone. Off everywhere
   * by default, so no existing caller changes: BDU shows the prefix in its
   * popup and omits it in the embedded page form, and 5002 serves both from one
   * component, so the difference has to be a prop.
   */
  hideCountryPrefix?: boolean;
};

const PhoneNoInput: React.FC<PhoneNoInputProps> = ({
  className,
  value,
  onBlur,
  onFocus,
  onChange,
  disabled,
  inputClassName,
  placeholder,
  prefixClassName,
  prefixWrapperClassName,
  hideCountryPrefix,
  label,
  embedCard,
  desktopFigmaVariant,
}) => {
  const enrollmentLocked = Boolean(disabled && desktopFigmaVariant);

  return (
    <>
      {label && (
        <label className="text-sm font-medium pl-3 text-gray-700">
          {label} <span className="text-secondary-main">*</span>
        </label>
      )}
      <div
        className={cn(
          "flex w-full min-w-0 box-border transition-colors",
          enrollmentLocked &&
            "h-9 min-h-9 max-h-9 cursor-not-allowed items-center rounded-md border-0 border-solid border-transparent bg-[#EEEBF7] px-0 py-0 font-poppins [-webkit-tap-highlight-color:transparent] transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)]",
          desktopFigmaVariant &&
            !enrollmentLocked &&
            "h-[36px] min-h-[36px] max-h-[36px] items-center gap-2 rounded-[4px] border-0 border-solid border-transparent bg-transparent px-4 py-0 font-poppins [-webkit-tap-highlight-color:transparent] transition-colors duration-150 ease-[cubic-bezier(0.4,0,0.2,1)] focus-within:outline-none",
          embedCard
            ? "h-[36px] min-h-[36px] max-h-[36px] items-center overflow-hidden rounded-[6px] border border-[#e5e7eb] bg-[#FFFFFF]"
            : !desktopFigmaVariant &&
                "items-center rounded border border-r-0 border-gray-300 pl-4",
          className,
        )}
      >
        {hideCountryPrefix ? null : (
        <div
          className={cn(
            "flex shrink-0 items-center",
            enrollmentLocked &&
              "justify-center gap-2 border-r border-[#E5E7EB] pr-3 pl-4",
            desktopFigmaVariant &&
              !enrollmentLocked &&
              "gap-[6px] border-r border-[rgba(124,134,152,0.35)] px-0 py-0 pr-3 pl-3",
            embedCard
              ? "border-r border-gray-200 px-3 py-0"
              : !desktopFigmaVariant && "justify-center gap-[8px] pr-3",
            prefixWrapperClassName,
          )}
        >
          <Flag width={28} height={20} />
          {desktopFigmaVariant && !enrollmentLocked ? (
            <>
              <span
                className={cn(
                  "whitespace-nowrap font-['Poppins',sans-serif] text-[14px] font-normal leading-5 tracking-[-0.064px] text-[#171719]",
                  "lg:text-[14px] lg:leading-5 lg:tracking-[-0.0853px] lg:text-[#7c8698]",
                )}
              >
                +91
              </span>
              <ChevronDown
                className="size-4 shrink-0 text-[#171719] lg:hidden"
                strokeWidth={2}
                aria-hidden
              />
            </>
          ) : (
            <span
              className={cn(
                embedCard
                  ? "text-[14px] leading-5 text-gray-600"
                  : "text-xs text-gray-800",
                enrollmentLocked && "text-xs text-gray-800",
                prefixClassName,
              )}
            >
              +91
            </span>
          )}
        </div>
        )}
        <Input
          value={value}
          onChange={(e) => {
            // Remove all non-digit characters (including e, E, +, -, .)
            const onlyNums = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
            onChange({ ...e, target: { ...e.target, value: onlyNums } });
          }}
          onKeyDown={(e) => {
            // Prevent 'e', 'E', '+', '-', '.' from being entered
            if (
              e.key === "e" ||
              e.key === "E" ||
              e.key === "+" ||
              e.key === "-" ||
              e.key === "."
            ) {
              e.preventDefault();
            }
          }}
          type="text"
          inputMode="numeric"
          placeholder={placeholder ?? "Phone Number"}
          className={cn(
            "min-w-0 grow box-border transition-colors",
            enrollmentLocked && [
              "!m-0 h-9 min-h-9 rounded-none rounded-r-md border-0 bg-[#EEEBF7] px-3 py-1 font-['Poppins',sans-serif] text-xs text-[#464646] shadow-sm",
              "placeholder:text-[#848484]",
              "outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0",
              "focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-[-2px] focus-visible:ring-offset-transparent",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
              "[&:autofill]:shadow-[inset_0_0_0px_1000px_#EEEBF7]",
              "[&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_#EEEBF7]",
            ],
            desktopFigmaVariant &&
              !enrollmentLocked && [
                "!m-0 h-full min-h-0 rounded-none border-0 bg-transparent px-0 py-2 text-[14px] font-medium leading-normal tracking-[0.24px] text-[#171719] shadow-none placeholder:text-[#848484] lg:text-[14px] lg:tracking-[0.32px] lg:text-[#373737]",
                "outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0",
                "focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0",
                "[&:autofill]:shadow-[inset_0_0_0px_1000px_#EEEBF7]",
                "[&:-webkit-autofill]:shadow-[inset_0_0_0px_1000px_#EEEBF7]",
                "[&:-webkit-autofill:hover]:shadow-[inset_0_0_0px_1000px_#EEEBF7]",
              ],
            embedCard
              ? [
                  "h-[36px] min-h-[36px] rounded-none border-0 bg-[#FFFFFF] px-3 text-[14px] leading-normal shadow-none",
                  "placeholder:text-[#848484]",
                  "focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0",
                ]
              : !desktopFigmaVariant && [
                  "grow rounded-l-none h-full border border-gray-300 rounded-[4px] pl-4 placeholder:text-gray-300",
                  "focus:outline-none focus:ring-2 focus:ring-black focus:border-black",
                  "focus-visible:ring-offset-[-2px] focus-visible:ring-offset-transparent",
                ],
            inputClassName,
          )}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
        />
      </div>
    </>
  );
};

export { PhoneNoInput };
 